<template>
  <WebChapterTemplate
    :page-title="pageTitle"
    :section-label="sectionLabel"
    :section-route="sectionRoute"
    :top-strip-image="topStripImage"
    :hero-background-image="heroBackgroundImage"
  >
    <div ref="stageMeasure" class="reconstructed-document">
      <div
          v-for="page in renderedPages"
          :key="page.pdfPage"
          class="reconstructed-page"
      >
        <div
          class="reconstructed-page__stage"
          :style="{ height: `${page.contentHeight * pageScale}px` }"
        >
          <div
            class="reconstructed-page__canvas"
            :style="{
              width: `${visibleWidth}px`,
              height: `${page.contentHeight}px`,
              transform: `scale(${pageScale})`
            }"
          >
            <div
              v-for="(image, index) in page.images"
              :key="`image-${page.pdfPage}-${index}`"
              class="reconstructed-image"
              :style="image.cropStyle"
            >
              <img
                :src="page.src"
                :alt="`第 ${page.pdfPage} 页图片 ${index + 1}`"
                :style="image.imageStyle"
              />
            </div>

            <span
              v-for="(node, index) in page.texts"
              :key="`text-${page.pdfPage}-${index}`"
              ref="textNodes"
              class="reconstructed-text"
              :data-target-width="node.targetWidth"
              :style="node.style"
            >
              {{ node.text }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </WebChapterTemplate>
</template>

<script>
import WebChapterTemplate from '@/components/WebChapterTemplate.vue';

export default {
  name: 'PdfReconstructedChapter',
  components: {
    WebChapterTemplate
  },
  props: {
    pageTitle: {
      type: String,
      required: true
    },
    sectionLabel: {
      type: String,
      required: true
    },
    sectionRoute: {
      type: [String, Object],
      default: '/lanhu_xuekeyukeyan'
    },
    pages: {
      type: Array,
      default: () => []
    },
    pageSize: {
      type: Object,
      default: () => ({
        width: 1322,
        height: 1003
      })
    },
    crop: {
      type: Object,
      default: () => ({
        left: 76,
        right: 76,
        top: 150,
        bottom: 98
      })
    },
    layoutXmlPath: {
      type: String,
      required: true
    },
    manualImages: {
      type: Object,
      default: () => ({})
    },
    imageInset: {
      type: Object,
      default: () => ({
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
      })
    },
    enhanceImageAdjacentText: {
      type: Boolean,
      default: true
    },
    bodyTextScale: {
      type: Number,
      default: 1.1
    },
    maxTextWidthScale: {
      type: Number,
      default: 1.2
    },
    topStripImage: {
      type: String,
      required: true
    },
    heroBackgroundImage: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      pageScale: 1,
      renderedPages: [],
      resizeObserver: null
    };
  },
  computed: {
    visibleHeight() {
      return Math.max(1, this.pageSize.height - this.crop.top - this.crop.bottom);
    },
    visibleWidth() {
      return Math.max(1, this.pageSize.width - this.crop.left - this.crop.right);
    }
  },
  mounted() {
    this.setupScaleSync();
    this.loadLayout();
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updatePageScale);
    }
  },
  methods: {
    getSiteSansFontFamily() {
      return 'PingFangSC-Regular, Roboto, Helvetica Neue, Helvetica, Tahoma, Arial, PingFang SC-Light, Microsoft YaHei, sans-serif';
    },
    setupScaleSync() {
      this.$nextTick(() => {
        this.updatePageScale();
        if (typeof ResizeObserver !== 'undefined' && this.$refs.stageMeasure) {
          this.resizeObserver = new ResizeObserver(() => this.updatePageScale());
          this.resizeObserver.observe(this.$refs.stageMeasure);
        } else if (typeof window !== 'undefined') {
          window.addEventListener('resize', this.updatePageScale, { passive: true });
        }
      });
    },
    updatePageScale() {
      const stage = this.$refs.stageMeasure;
      if (!stage) {
        return;
      }
      const width = stage.clientWidth || this.visibleWidth;
      this.pageScale = Math.min(1, width / this.visibleWidth);
    },
    normalizeFontFamily(pdfFontName = '') {
      const base = pdfFontName.includes('+') ? pdfFontName.split('+')[1] : pdfFontName;
      const name = String(base).toLowerCase();
      const siteSans = this.getSiteSansFontFamily();
      if (name.includes('timesnewroman')) {
        return siteSans;
      }
      if (name.includes('stxingkai')) {
        return '"STXingkai", "Kaiti SC", "KaiTi", cursive';
      }
      if (name.includes('fz') || name.includes('adobesong') || name.includes('song')) {
        return siteSans;
      }
      return siteSans;
    },
    resolveTextPresentation(font, box, imageBoxes) {
      const color = String(font.color || '').toLowerCase();
      const isHeading = font.size >= 20 || color === '#f25c19';
      const isNearImage = this.enhanceImageAdjacentText && this.isTextNearImage(box, imageBoxes);
      const isMinorMeta = !isHeading && (font.size <= 11 || color === '#b01319');

      if (isHeading) {
        return {
          family: font.family,
          size: Math.max(font.size, 22),
          weight: 700
        };
      }

      if (isMinorMeta) {
        return {
          family: font.family,
          size: Math.max(13 * this.bodyTextScale, 13),
          weight: 400
        };
      }

      if (isNearImage) {
        return {
          family: font.family,
          size: Math.max(17.5 * this.bodyTextScale, 18),
          weight: 500
        };
      }

      return {
        family: font.family,
        size: Math.max(16 * this.bodyTextScale, 16),
        weight: 400
      };
    },
    isTextNearImage(textBox, imageBoxes) {
      if (!textBox || !Array.isArray(imageBoxes) || !imageBoxes.length) {
        return false;
      }

      const textLeft = textBox.left;
      const textRight = textBox.left + textBox.width;
      const textTop = textBox.top;
      const textBottom = textBox.top + textBox.height;

      return imageBoxes.some((imageBox) => {
        const horizontalOverlap = Math.min(textRight, imageBox.right) - Math.max(textLeft, imageBox.left);
        const hasMeaningfulOverlap = horizontalOverlap > Math.min(textBox.width, imageBox.width) * 0.22;
        const verticalGap = Math.min(
          Math.abs(textTop - imageBox.bottom),
          Math.abs(textBottom - imageBox.top)
        );
        const sideGap = Math.min(
          Math.abs(textLeft - imageBox.right),
          Math.abs(textRight - imageBox.left)
        );

        if (hasMeaningfulOverlap && verticalGap <= 84) {
          return true;
        }

        const verticalOverlap = Math.min(textBottom, imageBox.bottom) - Math.max(textTop, imageBox.top);
        return verticalOverlap > Math.min(textBox.height, imageBox.height) * 0.35 && sideGap <= 54;
      });
    },
    clampToVisibleArea(left, top, width, height) {
      const visibleLeft = this.crop.left;
      const visibleTop = this.crop.top;
      const visibleRight = this.pageSize.width - this.crop.right;
      const visibleBottom = this.pageSize.height - this.crop.bottom;

      const x1 = Math.max(left, visibleLeft);
      const y1 = Math.max(top, visibleTop);
      const x2 = Math.min(left + width, visibleRight);
      const y2 = Math.min(top + height, visibleBottom);

      if (x2 <= x1 || y2 <= y1) {
        return null;
      }

      return {
        left: x1 - visibleLeft,
        top: y1 - visibleTop,
        width: x2 - x1,
        height: y2 - y1,
        sourceLeft: x1,
        sourceTop: y1
      };
    },
    overlapArea(boxA, boxB) {
      const width = Math.min(boxA.right, boxB.right) - Math.max(boxA.left, boxB.left);
      const height = Math.min(boxA.bottom, boxB.bottom) - Math.max(boxA.top, boxB.top);

      if (width <= 0 || height <= 0) {
        return 0;
      }

      return width * height;
    },
    isTextEmbeddedInImage(textBox, imageBoxes) {
      if (!textBox || !Array.isArray(imageBoxes) || !imageBoxes.length) {
        return false;
      }

      const area = Math.max(1, textBox.width * textBox.height);
      return imageBoxes.some((imageBox) => this.overlapArea(textBox, imageBox) / area > 0.55);
    },
    isDecorativePageNumber(rawText, pageNumber) {
      return rawText === String(pageNumber);
    },
    buildImageLayout(left, top, width, height, displayOffset = {}) {
      const insetLeft = Math.max(0, Number(this.imageInset.left || 0));
      const insetRight = Math.max(0, Number(this.imageInset.right || 0));
      const insetTop = Math.max(0, Number(this.imageInset.top || 0));
      const insetBottom = Math.max(0, Number(this.imageInset.bottom || 0));
      const offsetX = Number(displayOffset.x || 0);
      const offsetY = Number(displayOffset.y || 0);
      const adjustedLeft = left + insetLeft;
      const adjustedTop = top + insetTop;
      const adjustedWidth = Math.max(1, width - insetLeft - insetRight);
      const adjustedHeight = Math.max(1, height - insetTop - insetBottom);
      const box = this.clampToVisibleArea(adjustedLeft, adjustedTop, adjustedWidth, adjustedHeight);

      if (!box) {
        return null;
      }

      return {
        originalTop: box.top + offsetY,
        left: box.left + offsetX,
        top: box.top + offsetY,
        width: box.width,
        height: box.height,
        cropStyle: {
          left: `${box.left + offsetX}px`,
          top: `${box.top + offsetY}px`,
          width: `${box.width}px`,
          height: `${box.height}px`
        },
        imageStyle: {
          width: `${this.pageSize.width}px`,
          height: `${this.pageSize.height}px`,
          transform: `translate(-${box.sourceLeft}px, -${box.sourceTop}px)`
        }
      };
    },
    trimManualRegion(region, textNodes) {
      const regionLeft = region.left;
      const regionRight = region.left + region.width;
      const regionTop = region.top;
      const regionBottom = region.top + region.height;

      const overlappingTexts = textNodes
        .filter((node) => {
          const nodeLeft = node.left;
          const nodeRight = node.left + node.width;
          const overlapWidth = Math.min(regionRight, nodeRight) - Math.max(regionLeft, nodeLeft);
          if (overlapWidth <= Math.min(node.width, region.width) * 0.3) {
            return false;
          }
          return node.top > regionTop + region.height * 0.45 && node.top < regionBottom;
        })
        .sort((a, b) => a.top - b.top);

      const trimTarget = overlappingTexts.length ? overlappingTexts[0].top - 8 : regionBottom - 16;
      const trimmedBottom = Math.max(regionTop + 40, Math.min(regionBottom, trimTarget));

      return {
        ...region,
        height: trimmedBottom - regionTop
      };
    },
    resolveTargetWidth(box, imageBoxes) {
      const visibleRight = this.visibleWidth;
      let maxAvailableWidth = Math.max(24, visibleRight - box.left - 12);
      const captionAnchor = this.findCaptionAnchorImage(box, imageBoxes);

      if (Array.isArray(imageBoxes) && imageBoxes.length) {
        const boxTop = box.top;
        const boxBottom = box.top + box.height;
        const overlappingImage = imageBoxes
          .filter((imageBox) => {
            const verticalOverlap = Math.min(boxBottom, imageBox.bottom) - Math.max(boxTop, imageBox.top);
            return imageBox.left > box.left && verticalOverlap > Math.min(box.height, imageBox.height) * 0.2;
          })
          .sort((a, b) => a.left - b.left)[0];

        if (overlappingImage) {
          maxAvailableWidth = Math.min(maxAvailableWidth, Math.max(24, overlappingImage.left - box.left - 18));
        }
      }

      if (captionAnchor) {
        const anchorSpan = Math.max(40, captionAnchor.width - 16);
        maxAvailableWidth = Math.min(maxAvailableWidth, anchorSpan);
      }

      return Math.max(box.width, Math.min(box.width * this.maxTextWidthScale, maxAvailableWidth));
    },
    findCaptionAnchorImage(box, imageBoxes, minGap = -10, maxGap = 72) {
      if (!Array.isArray(imageBoxes) || !imageBoxes.length) {
        return null;
      }

      const boxLeft = box.left;
      const boxRight = box.left + box.width;
      const boxTop = box.top;

      return imageBoxes
        .map((imageBox) => {
          const horizontalOverlap = Math.min(boxRight, imageBox.right) - Math.max(boxLeft, imageBox.left);
          const horizontalRatio = horizontalOverlap / Math.max(1, Math.min(box.width, imageBox.width));
          const verticalGap = boxTop - imageBox.bottom;

          return {
            imageBox,
            horizontalRatio,
            verticalGap
          };
        })
        .filter(({ horizontalRatio }) => horizontalRatio > 0.22)
        .filter(({ verticalGap }) => verticalGap >= minGap && verticalGap <= maxGap)
        .sort((a, b) => a.verticalGap - b.verticalGap)[0]?.imageBox || null;
    },
    normalizeInlineImageTextPositions(texts, images) {
      const imageBoxes = images.map((image) => ({
        left: image.left,
        top: image.top,
        width: image.width,
        height: image.height,
        right: image.left + image.width,
        bottom: image.top + image.height
      }));

      return texts.map((textNode) => {
        const textBottom = textNode.top + this.getTextRenderHeight(textNode);
        const inlineImage = imageBoxes
          .filter((imageBox) => {
            const verticalOverlap = Math.min(textBottom, imageBox.bottom) - Math.max(textNode.top, imageBox.top);
            if (verticalOverlap <= Math.min(this.getTextRenderHeight(textNode), imageBox.height) * 0.35) {
              return false;
            }

            if (imageBox.left > textNode.left + 40 || imageBox.right <= textNode.left + 8) {
              return false;
            }

            return imageBox.right < textNode.left + textNode.targetWidth - 16;
          })
          .sort((a, b) => b.right - a.right)[0];

        if (!inlineImage) {
          return textNode;
        }

        const left = inlineImage.right + 14;
        const width = Math.max(24, this.visibleWidth - left - 12);

        return {
          ...textNode,
          left,
          targetWidth: width,
          style: {
            ...textNode.style,
            left: `${left}px`,
            width: `${width}px`
          }
        };
      });
    },
    getTextRenderHeight(textNode) {
      return textNode.renderHeight || textNode.lineHeight || textNode.targetHeight || 0;
    },
    shouldMergeTextNodes(previous, current) {
      if (!previous || !current) {
        return false;
      }

      if (previous.style.fontWeight >= 700 || current.style.fontWeight >= 700) {
        return false;
      }

      const previousBottom = previous.top + this.getTextRenderHeight(previous);
      const verticalGap = current.top - previousBottom;
      if (verticalGap < -18 || verticalGap > 24) {
        return false;
      }

      const leftDelta = Math.abs(current.left - previous.left);
      if (leftDelta > 28) {
        return false;
      }

      const previousRight = previous.left + previous.targetWidth;
      const currentRight = current.left + current.targetWidth;
      const rightDelta = Math.abs(currentRight - previousRight);
      if (rightDelta > 260) {
        return false;
      }

      const previousSize = Number.parseFloat(previous.style.fontSize || '0');
      const currentSize = Number.parseFloat(current.style.fontSize || '0');
      if (Math.abs(previousSize - currentSize) > 1.5) {
        return false;
      }

      if (previous.style.color !== current.style.color) {
        return false;
      }

      if (previous.style.fontFamily !== current.style.fontFamily) {
        return false;
      }

      return true;
    },
    mergeTextNodes(texts) {
      const sortedTexts = [...texts].sort((a, b) => {
        if (a.top === b.top) {
          return a.left - b.left;
        }
        return a.top - b.top;
      });

      const merged = [];

      sortedTexts.forEach((textNode) => {
        let previousIndex = -1;
        for (let index = merged.length - 1; index >= 0; index -= 1) {
          if (this.shouldMergeTextNodes(merged[index], textNode)) {
            previousIndex = index;
            break;
          }
        }

        const previous = previousIndex >= 0 ? merged[previousIndex] : null;
        if (!previous) {
          merged.push({
            ...textNode,
            lineCount: textNode.lineCount || 1,
            renderHeight: textNode.renderHeight || textNode.lineHeight
          });
          return;
        }

        const left = Math.min(previous.left, textNode.left);
        const right = Math.max(previous.left + previous.targetWidth, textNode.left + textNode.targetWidth);
        const lineCount = (previous.lineCount || 1) + 1;
        const lineHeight = previous.lineHeight;
        const renderHeight = lineCount * lineHeight;

        merged[previousIndex] = {
          ...previous,
          text: `${previous.text}\n${textNode.text}`,
          left,
          targetWidth: right - left,
          targetHeight: renderHeight,
          lineCount,
          renderHeight,
          style: {
            ...previous.style,
            left: `${left}px`,
            width: `${right - left}px`,
            whiteSpace: 'pre',
            lineHeight: `${lineHeight}px`
          }
        };
      });

      return merged;
    },
    normalizeTextVerticalSpacing(texts) {
      const sortedTexts = [...texts].sort((a, b) => {
        if (a.top === b.top) {
          return a.left - b.left;
        }
        return a.top - b.top;
      });

      sortedTexts.forEach((current, index) => {
        let requiredTop = current.top;

        for (let i = 0; i < index; i += 1) {
          const previous = sortedTexts[i];
          const overlapWidth = Math.min(
            current.left + current.targetWidth,
            previous.left + previous.targetWidth
          ) - Math.max(current.left, previous.left);

          if (overlapWidth <= Math.min(current.targetWidth, previous.targetWidth) * 0.28) {
            continue;
          }

          const gap = Math.max(10, Math.round(Math.min(previous.lineHeight, current.lineHeight) * 0.24));
          requiredTop = Math.max(requiredTop, previous.top + this.getTextRenderHeight(previous) + gap);
        }

        current.top = requiredTop;
      });

      return sortedTexts;
    },
    normalizeCaptionTextPositions(texts, images) {
      const imageBoxes = images.map((image) => ({
        left: image.left,
        top: image.top,
        width: image.width,
        height: image.height,
        right: image.left + image.width,
        bottom: image.top + image.height
      }));

      return texts.map((textNode) => {
        const anchor = this.findCaptionAnchorImage(
          {
            left: textNode.left,
            top: textNode.top,
            width: textNode.targetWidth,
            height: textNode.targetHeight
          },
          imageBoxes,
          -90,
          90
        );

        if (!anchor) {
          return textNode;
        }

        const minTop = anchor.bottom + 10;
        if (textNode.top >= minTop) {
          return textNode;
        }

        return {
          ...textNode,
          top: minTop,
          style: {
            ...textNode.style,
            top: `${minTop}px`
          }
        };
      });
    },
    resolveResidualImageTextOverlaps(texts, images) {
      const imageBoxes = images.map((image) => ({
        left: image.left,
        top: image.top,
        width: image.width,
        height: image.height,
        right: image.left + image.width,
        bottom: image.top + image.height
      }));

      return texts.map((textNode) => {
        const textRight = textNode.left + textNode.targetWidth;
        const textBottom = textNode.top + this.getTextRenderHeight(textNode);
        const overlappingImages = imageBoxes.filter((imageBox) => {
          const overlapWidth = Math.min(textRight, imageBox.right) - Math.max(textNode.left, imageBox.left);
          const overlapHeight = Math.min(textBottom, imageBox.bottom) - Math.max(textNode.top, imageBox.top);
          return overlapWidth > 4 && overlapHeight > 4;
        });

        if (!overlappingImages.length) {
          return textNode;
        }

        const minTop = Math.max(...overlappingImages.map((imageBox) => imageBox.bottom)) + 10;
        if (textNode.top >= minTop) {
          return textNode;
        }

        return {
          ...textNode,
          top: minTop,
          style: {
            ...textNode.style,
            top: `${minTop}px`
          }
        };
      });
    },
    normalizeImageVerticalSpacing(images, texts) {
      const sortedImages = [...images]
        .map((image) => ({ ...image }))
        .sort((a, b) => {
          if (a.originalTop === b.originalTop) {
            return a.left - b.left;
          }
          return a.originalTop - b.originalTop;
        });

      const rows = [];
      sortedImages.forEach((image) => {
        const existingRow = rows.find((row) => Math.abs(row.originalTop - image.originalTop) <= 28);
        if (existingRow) {
          existingRow.images.push(image);
          existingRow.originalTop = Math.min(existingRow.originalTop, image.originalTop);
          return;
        }

        rows.push({
          originalTop: image.originalTop,
          images: [image]
        });
      });

      rows.sort((a, b) => a.originalTop - b.originalTop);

      rows.forEach((row, rowIndex) => {
        let requiredTop = row.originalTop;
        const rowLeft = Math.min(...row.images.map((image) => image.left));
        const rowRight = Math.max(...row.images.map((image) => image.left + image.width));

        texts.forEach((textNode) => {
          const textRight = textNode.left + textNode.targetWidth;
          const overlapWidth = Math.min(rowRight, textRight) - Math.max(rowLeft, textNode.left);
          if (overlapWidth <= Math.min(rowRight - rowLeft, textNode.targetWidth) * 0.16) {
            return;
          }

          const textBottom = textNode.top + this.getTextRenderHeight(textNode);
          if (row.originalTop < textBottom + 12 && textNode.top <= row.originalTop) {
            requiredTop = Math.max(requiredTop, textBottom + 12);
          }
        });

        for (let i = 0; i < rowIndex; i += 1) {
          const previousRow = rows[i];
          const previousLeft = Math.min(...previousRow.images.map((image) => image.left));
          const previousRight = Math.max(...previousRow.images.map((image) => image.left + image.width));
          const overlapWidth = Math.min(rowRight, previousRight) - Math.max(rowLeft, previousLeft);

          if (overlapWidth <= Math.min(rowRight - rowLeft, previousRight - previousLeft) * 0.16) {
            continue;
          }

          const previousBottom = Math.max(...previousRow.images.map((image) => image.top + image.height));
          requiredTop = Math.max(requiredTop, previousBottom + 12);
        }

        const delta = requiredTop - row.originalTop;
        row.images.forEach((image) => {
          if (!delta) {
            return;
          }

          image.top = image.originalTop + delta;
          image.cropStyle = {
            ...image.cropStyle,
            top: `${image.top}px`
          };
        });
      });

      return sortedImages;
    },
    resolvePageContentHeight(images, texts) {
      const imageBottom = images.reduce((maxBottom, image) => Math.max(maxBottom, image.top + image.height), 0);
      const textBottom = texts.reduce((maxBottom, textNode) => Math.max(maxBottom, textNode.top + this.getTextRenderHeight(textNode)), 0);
      return Math.max(this.visibleHeight, imageBottom + 24, textBottom + 24);
    },
    async loadLayout() {
      try {
        const response = await fetch(this.layoutXmlPath, { cache: 'no-cache' });
        if (!response.ok) {
          this.renderedPages = this.pages.map((page) => ({ ...page, images: [], texts: [] }));
          return;
        }

        const xmlText = await response.text();
        const xmlDoc = new DOMParser().parseFromString(xmlText, 'application/xml');
        const pageNodes = Array.from(xmlDoc.querySelectorAll('page'));
        const layoutMap = {};

        pageNodes.forEach((pageNode) => {
          const pageNumber = Number(pageNode.getAttribute('number'));
          const fontSpecs = {};

          Array.from(pageNode.querySelectorAll('fontspec')).forEach((fontNode) => {
            const id = fontNode.getAttribute('id');
            fontSpecs[id] = {
              size: Number(fontNode.getAttribute('size') || 14),
              family: this.normalizeFontFamily(fontNode.getAttribute('family') || ''),
              color: fontNode.getAttribute('color') || '#000000'
            };
          });

          const rawTexts = Array.from(pageNode.querySelectorAll('text'))
            .map((textNode) => {
              const rawText = (textNode.textContent || '').replace(/\u00a0/g, ' ').trim();
              if (!rawText || this.isDecorativePageNumber(rawText, pageNumber)) {
                return null;
              }

              const left = Number(textNode.getAttribute('left') || 0);
              const top = Number(textNode.getAttribute('top') || 0);
              const width = Number(textNode.getAttribute('width') || 0);
              const height = Number(textNode.getAttribute('height') || 0);

              return {
                rawText,
                left,
                top,
                width: Math.max(width, 12),
                height: Math.max(height, 14),
                fontId: textNode.getAttribute('font')
              };
            })
            .filter(Boolean);

          const images = Array.from(pageNode.querySelectorAll('image'))
            .map((imageNode) => {
              const left = Number(imageNode.getAttribute('left') || 0);
              const top = Number(imageNode.getAttribute('top') || 0);
              const width = Number(imageNode.getAttribute('width') || 0);
              const height = Number(imageNode.getAttribute('height') || 0);
              return this.buildImageLayout(left, top, width, height);
            })
            .filter(Boolean);

          const manualLayouts = (this.manualImages[pageNumber] || [])
            .map((region) => this.trimManualRegion(region, rawTexts))
            .map((region) => this.buildImageLayout(
              region.left,
              region.top,
              region.width,
              region.height,
              { x: region.displayOffsetX, y: region.displayOffsetY }
            ))
            .filter(Boolean);

          const imageLayouts = [...images, ...manualLayouts];
          const imageBoxes = imageLayouts.map((image) => ({
            left: image.left,
            top: image.top,
            width: image.width,
            height: image.height,
            right: image.left + image.width,
            bottom: image.top + image.height
          }));

          const texts = this.mergeTextNodes(this.normalizeTextVerticalSpacing(rawTexts
            .map((textNode) => {
              const box = this.clampToVisibleArea(textNode.left, textNode.top, textNode.width, textNode.height);

              if (!box) {
                return null;
              }

              if (this.isTextEmbeddedInImage(box, imageBoxes)) {
                return null;
              }

              const font = fontSpecs[textNode.fontId] || {
                size: 14,
                family: this.getSiteSansFontFamily(),
                color: '#000000'
              };
              const display = this.resolveTextPresentation(font, box, imageBoxes);
              const targetWidth = this.resolveTargetWidth(box, imageBoxes);
              const lineHeight = Math.max(box.height + 2, Math.round(display.size * 1.28));

              return {
                text: textNode.rawText,
                left: box.left,
                top: box.top,
                targetWidth,
                targetHeight: box.height,
                lineHeight,
                renderHeight: lineHeight,
                lineCount: 1,
                style: {
                  left: `${box.left}px`,
                  top: `${box.top}px`,
                  width: textNode.width > 0 ? `${targetWidth}px` : 'auto',
                  color: font.color,
                  fontSize: `${display.size}px`,
                  lineHeight: `${lineHeight}px`,
                  fontFamily: display.family,
                  fontWeight: display.weight,
                  whiteSpace: 'pre'
                }
              };
            })
            .filter(Boolean)))
            .map((textNode) => ({
              ...textNode,
              style: {
                ...textNode.style,
                top: `${textNode.top}px`
              }
            }));

          const normalizedImages = this.normalizeImageVerticalSpacing(imageLayouts, texts);
          const normalizedTexts = this.normalizeTextVerticalSpacing(
            this.resolveResidualImageTextOverlaps(
              this.normalizeCaptionTextPositions(
                this.normalizeInlineImageTextPositions(texts, normalizedImages),
                normalizedImages
              ),
              normalizedImages
            )
          ).map((textNode) => ({
            ...textNode,
            style: {
              ...textNode.style,
              top: `${textNode.top}px`
            }
          }));
          layoutMap[pageNumber] = {
            images: normalizedImages,
            texts: normalizedTexts,
            contentHeight: this.resolvePageContentHeight(normalizedImages, normalizedTexts)
          };
        });

        this.renderedPages = this.pages.map((page) => ({
          ...page,
          images: (layoutMap[page.pdfPage] && layoutMap[page.pdfPage].images) || [],
          texts: (layoutMap[page.pdfPage] && layoutMap[page.pdfPage].texts) || [],
          contentHeight: (layoutMap[page.pdfPage] && layoutMap[page.pdfPage].contentHeight) || this.visibleHeight
        }));

        this.$nextTick(() => {
          this.updatePageScale();
          this.fitTextNodes();
          if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => {
              this.$nextTick(() => {
                this.fitTextNodes();
              });
            });
          }
        });
      } catch (error) {
        this.renderedPages = this.pages.map((page) => ({ ...page, images: [], texts: [] }));
      }
    },
    fitTextNodes() {
      const textNodes = Array.isArray(this.$refs.textNodes)
        ? this.$refs.textNodes
        : this.$refs.textNodes
          ? [this.$refs.textNodes]
          : [];

      textNodes.forEach((node) => {
        if (!node) {
          return;
        }

        node.style.transform = 'none';
        node.style.transformOrigin = 'left top';

        const targetWidth = Number(node.dataset.targetWidth || 0);
        if (!targetWidth) {
          return;
        }

        let fontSize = Number.parseFloat(node.style.fontSize || '0');
        const minFontSize = 14;

        while (fontSize > minFontSize && node.scrollWidth > targetWidth + 2) {
          fontSize -= 0.5;
          node.style.fontSize = `${fontSize}px`;
          node.style.lineHeight = `${Math.round(fontSize * 1.18)}px`;
        }
      });
    }
  }
};
</script>

<style scoped>
.reconstructed-document {
  display: grid;
  gap: 28px;
}

.reconstructed-page {
  padding: 18px 18px 24px;
  border-radius: 14px;
  border: 1px solid rgba(179, 145, 101, 0.18);
  background:
    linear-gradient(140deg, rgba(248, 242, 233, 0.84), rgba(255, 255, 255, 0) 42%),
    linear-gradient(180deg, #fff, rgba(253, 250, 246, 0.96));
  box-shadow: 0 14px 30px rgba(59, 41, 17, 0.05);
}

.reconstructed-page__stage {
  width: 100%;
  overflow: hidden;
  border-radius: 10px;
  background: #fff;
  box-shadow: inset 0 0 0 1px rgba(179, 145, 101, 0.12);
}

.reconstructed-page__canvas {
  position: relative;
  transform-origin: top left;
  background: #fff;
}

.reconstructed-image {
  position: absolute;
  overflow: hidden;
  z-index: 1;
}

.reconstructed-image img {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
}

.reconstructed-text {
  position: absolute;
  z-index: 2;
  display: inline-block;
  white-space: pre;
  text-align: left;
  transform-origin: left top;
  will-change: transform;
}

@media (max-width: 768px) {
  .reconstructed-page {
    padding: 14px 14px 18px;
  }
}
</style>
